import React, { useState, useRef, useCallback } from 'react';
import './SimpleFallbackEditor.css';

// Force refresh component
const SimpleFallbackEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [content, setContent] = useState(value || '');

  const handleContentChange = useCallback((event) => {
    const newContent = event.target.innerHTML;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  }, [onChange]);

  const handleKeyUp = useCallback((event) => {
    // Trigger onChange on every keystroke for real-time autosave
    const newContent = event.target.innerHTML;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  }, [onChange]);

  const formatText = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  }, []);

  const insertText = useCallback((text) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    editorRef.current.focus();
  }, []);

  React.useEffect(() => {
    if (editorRef.current && value !== content) {
      editorRef.current.innerHTML = value || '';
      setContent(value || '');
    }
  }, [value, content]);

  return (
    <div 
      className="simple-fallback-editor"
      style={{
        width: '100%',
        margin: '0',
        padding: '0',
        fontFamily: "'PT Sans', sans-serif"
      }}
    >
      <div 
        className="editor-toolbar"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          background: 'rgba(75, 196, 185, 0.1)',
          border: '2px solid #4BC4B9',
          borderBottom: '0px',
          borderRadius: '20px 20px 0 0',
          flexWrap: 'wrap',
          margin: '0'
        }}
      >
        <button 
          type="button"
          onClick={() => formatText('bold')}
          title="Bold"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <strong>B</strong>
        </button>
        <button 
          type="button"
          onClick={() => formatText('italic')}
          title="Italic"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <em>I</em>
        </button>
        <button 
          type="button"
          onClick={() => formatText('underline')}
          title="Underline"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <u>U</u>
        </button>
        <span 
          className="toolbar-separator"
          style={{
            width: '1px',
            height: '24px',
            background: '#4BC4B9',
            margin: '0 8px'
          }}
        >|</span>
        <button 
          type="button"
          onClick={() => formatText('formatBlock', 'h1')}
          title="Heading 1"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          H1
        </button>
        <button 
          type="button"
          onClick={() => formatText('formatBlock', 'h2')}
          title="Heading 2"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          H2
        </button>
        <button 
          type="button"
          onClick={() => formatText('formatBlock', 'p')}
          title="Paragraph"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          P
        </button>
        <span 
          className="toolbar-separator"
          style={{
            width: '1px',
            height: '24px',
            background: '#4BC4B9',
            margin: '0 8px'
          }}
        >|</span>
        <button 
          type="button"
          onClick={() => formatText('insertUnorderedList')}
          title="Bullet List"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          • List
        </button>
        <button 
          type="button"
          onClick={() => formatText('insertOrderedList')}
          title="Numbered List"
          className="toolbar-button"
          style={{
            background: 'transparent',
            border: '1px solid #4BC4B9',
            borderRadius: '8px',
            color: '#4BC4B9',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            padding: '6px 10px',
            transition: 'all 0.2s ease',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          1. List
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable={true}
        suppressContentEditableWarning={true}
        className="editor-content"
        onInput={handleContentChange}
        onKeyUp={handleKeyUp}
        onBlur={handleContentChange}
        placeholder={placeholder || "Start typing your note..."}
        style={{
          minHeight: '400px',
          padding: '20px',
          border: '2px solid #4BC4B9',
          borderTop: '0px',
          borderRadius: '0 0 20px 20px',
          backgroundColor: '#1C2025',
          color: '#e1e4e8',
          fontSize: '16px',
          lineHeight: '1.6',
          fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace",
          outline: 'none',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          resize: 'vertical',
          overflowY: 'auto',
          marginTop: '0px'
        }}
      />
    </div>
  );
};

export default SimpleFallbackEditor;
